import React from "react";
import Input from "../../../controls/Input";
import SelectMenu from "../../../controls/SelectMenu";
import { InputNumber } from "primereact/inputnumber";
import { useForm } from "../../useForm";
import { yesOrNoOptions, roundCurrency } from "../../../../shared/utility";
import { makePostApiCall } from "../../../../shared/makeApiCall";
import {
  dropdownForPayload,
  normalizeDropdownOption,
} from "../../../../shared/locationDropdowns";
import ProgressModal from "../../../modals/ProgressModal";

interface SelectOption<T = number | string> {
  value: T;
  label: string;
}

interface DropdownOption {
  value: number | string | null;
  label: string;
}

interface ReceivingInvoiceProductRow {
  assembly_item_id: number;
  product_name?: string;
  received_amount?: string | number | null;
  unit_of_measure?: string | null;
  unit_cost?: string | number | null;
  total_cost?: string | number | null;
  product_temperature?: string | null;
  product_condition?: number | null;
  product_quality?: number | null;
  cfia_meat?: number | SelectOption | null;
  lab_results?: number | SelectOption | null;
  is_halal?: number | SelectOption | null;
  zone?: unknown;
}

interface EditProductFormValues {
  received_amount: number | null;
  unit_cost: number | null;
  total_cost: number | null;
  product_temperature: string;
  product_condition: SelectOption<number>;
  product_quality: SelectOption<number>;
  cfia_meat: SelectOption<number>;
  lab_results: SelectOption<number>;
  is_halal: SelectOption<number>;
  zone: DropdownOption | null;
}

interface SlideOverState {
  open: boolean;
  childComponent: React.ReactNode;
}

interface UpdateProductApiResponse {
  receiving_items: unknown[];
}

export interface EditProductSlideOverProps {
  product: ReceivingInvoiceProductRow;
  invoiceId: number | string;
  invoiceLocation: DropdownOption | null;
  zoneOptions: DropdownOption[];
  setSlideOver: React.Dispatch<React.SetStateAction<SlideOverState>>;
  setInvoiceItems: (items: unknown[]) => void;
  setEmptyModelOpen: React.Dispatch<
    React.SetStateAction<{ open: boolean; childComponent: React.ReactNode }>
  >;
  showErrorModal: (message: string) => void;
  onSaveSuccess?: () => void;
}

const qualityConditionOptions: SelectOption<number>[] = [
  { value: 0, label: "N/A" },
  { value: 1, label: "Satisfactory" },
  { value: 2, label: "NOT Satisfactory" },
];

function findConditionOption(value: number): SelectOption<number> {
  return (
    qualityConditionOptions.find((c) => c.value === value) ?? {
      value: 1,
      label: "Satisfactory",
    }
  );
}

const receivingFieldClassName =
  "mt-1 block w-full rounded-xl border border-[#E2E8F0] bg-white px-3 py-2.5 text-sm text-[#0F172A] shadow-sm transition focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/10 disabled:bg-[#F6F8FB] disabled:text-[#64748B]";

const receivingPrimaryButtonClass =
  "inline-flex items-center justify-center rounded-xl border border-[#2563EB] bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:border-[#1D4ED8] hover:bg-[#1D4ED8]";

const receivingNeutralButtonClass =
  "inline-flex items-center justify-center rounded-xl border border-[#E2E8F0] bg-white px-4 py-2 text-sm font-semibold text-[#0F172A] shadow-sm transition hover:border-[#94A3B8] hover:bg-[#F6F8FB]";

function findYesNoOption(
  value: number | SelectOption | null | undefined,
): SelectOption<number> {
  const raw =
    value != null && typeof value === "object" && "value" in value
      ? value.value
      : value;
  const n = Number(raw);
  const key = Number.isFinite(n) ? n : raw;
  return (
    yesOrNoOptions.find((o) => o.value === key) ?? {
      label: "N/A",
      value: 0,
    }
  );
}

function toNumberOrNull(
  value: string | number | null | undefined,
): number | null {
  if (value == null || value === "") {
    return null;
  }
  const n = typeof value === "number" ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : null;
}

function getApiErrorMessage(error: unknown): string {
  if (error && typeof error === "object") {
    const err = error as {
      response?: { data?: { message?: string } };
      message?: string;
    };
    return (
      err.response?.data?.message || err.message || "Failed to update product."
    );
  }
  return "Failed to update product.";
}

export default function EditProductSlideOver({
  product,
  invoiceId,
  invoiceLocation,
  zoneOptions,
  setSlideOver,
  setInvoiceItems,
  setEmptyModelOpen,
  showErrorModal,
  onSaveSuccess,
}: EditProductSlideOverProps) {
  const groupZone = normalizeDropdownOption(product?.zone);

  const initialFValues: EditProductFormValues = {
    received_amount: toNumberOrNull(product?.received_amount),
    unit_cost: toNumberOrNull(product?.unit_cost),
    total_cost: toNumberOrNull(product?.total_cost),
    product_temperature: product?.product_temperature ?? "",
    product_condition:
      product?.product_condition != null
        ? findConditionOption(product.product_condition)
        : { value: 1, label: "Satisfactory" },
    product_quality:
      product?.product_quality != null
        ? findConditionOption(product.product_quality)
        : { value: 1, label: "Satisfactory" },
    cfia_meat:
      product?.cfia_meat != null
        ? findYesNoOption(product.cfia_meat)
        : { value: 0, label: "N/A" },
    lab_results:
      product?.lab_results != null
        ? findYesNoOption(product.lab_results)
        : { value: 0, label: "N/A" },
    is_halal:
      product?.is_halal != null
        ? findYesNoOption(product.is_halal)
        : { value: 0, label: "N/A" },
    zone: normalizeDropdownOption(product?.zone),
  };

  const validate = () => true;

  const { values, setValues, handleInputChange } = useForm(
    initialFValues,
    false,
    validate,
  );

  const handleUnitCostChange = (value: number | null | undefined) => {
    const unitCost = value ?? null;
    const currentQuantity = Number(values.received_amount);
    if (
      unitCost != null &&
      Number.isFinite(currentQuantity) &&
      currentQuantity > 0
    ) {
      setValues({
        ...values,
        unit_cost: unitCost,
        total_cost: roundCurrency(unitCost * currentQuantity),
      });
    } else {
      setValues({ ...values, unit_cost: unitCost });
    }
  };

  const handleReceivedAmountChange = (value: number | null | undefined) => {
    const receivedAmount = value ?? null;
    setValues({
      ...values,
      received_amount: receivedAmount,
      total_cost:
        receivedAmount != null && values.unit_cost != null
          ? roundCurrency(receivedAmount * values.unit_cost)
          : values.total_cost,
    });
  };

  const handleTotalCostChange = (value: number | null | undefined) => {
    const totalCost = value ?? null;
    const currentQuantity = Number(values.received_amount);
    if (
      totalCost != null &&
      Number.isFinite(currentQuantity) &&
      currentQuantity > 0
    ) {
      setValues({
        ...values,
        total_cost: totalCost,
        unit_cost: roundCurrency(totalCost / currentQuantity),
      });
    } else {
      setValues({ ...values, total_cost: totalCost });
    }
  };

  const onSave = async () => {
    if (values.zone?.value && !invoiceLocation?.value) {
      showErrorModal(
        "Receiving invoice location is required when a zone is selected.",
      );
      return;
    }

    setSlideOver({ open: false, childComponent: null });
    const payload = {
      new_unit_cost: values.unit_cost,
      new_total_cost: values.total_cost,
      new_received_amount: values.received_amount,
      group_zone_id: Number(groupZone?.value ?? 0),
      product_temperature: values.product_temperature || null,
      product_condition: values.product_condition?.value ?? null,
      product_quality: values.product_quality?.value ?? null,
      cfia_meat: values.cfia_meat?.value ?? null,
      lab_results: values.lab_results?.value ?? null,
      is_halal: values.is_halal?.value ?? null,
      zone_id: dropdownForPayload(values.zone)?.value ?? 0,
    };

    setEmptyModelOpen({
      open: true,
      childComponent: <ProgressModal message="Updating..." />,
    });
    try {
      const result = await makePostApiCall(
        `updateReceivingAssemblyItemGroup/${invoiceId}/${product.assembly_item_id}`,
        payload,
      );
      if ("success" in result && result.success && result.data) {
        const data = result.data as UpdateProductApiResponse;
        setInvoiceItems(data.receiving_items);
        onSaveSuccess?.();
      }
    } catch (error: unknown) {
      showErrorModal(getApiErrorMessage(error));
    } finally {
      setEmptyModelOpen({ open: false, childComponent: null });
    }
  };

  const onCancel = () => {
    setSlideOver({ open: false, childComponent: null });
  };

  return (
    <div className="flex h-full flex-col divide-y divide-[#E2E8F0] bg-white">
      <div className="flex-1 overflow-auto bg-[#F6F8FB] p-6">
        <h2 className="mb-4 text-xl font-semibold tracking-tight text-[#0F172A]">
          Edit Product - {product?.product_name}
        </h2>

        {product?.received_amount != null && product.received_amount !== "" ? (
          <p className="mb-4 text-sm text-[#64748B]">
            Total received:{" "}
            <span className="font-medium text-[#0F172A]">
              {product.received_amount}
              {product.unit_of_measure ? ` ${product.unit_of_measure}` : ""}
            </span>
          </p>
        ) : null}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155]">
              Total Unit
            </label>
            <InputNumber
              value={values.received_amount}
              onValueChange={(e) => handleReceivedAmountChange(e.value)}
              min={0}
              className="w-full"
              inputClassName={receivingFieldClassName}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-1">
              Unit Cost
            </label>
            <InputNumber
              value={values.unit_cost}
              onValueChange={(e) => handleUnitCostChange(e.value)}
              mode="currency"
              currency="USD"
              locale="en-US"
              minFractionDigits={2}
              maxFractionDigits={4}
              className="w-full"
              inputClassName={receivingFieldClassName}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-1">
              Total Cost
            </label>
            <InputNumber
              value={values.total_cost}
              onValueChange={(e) => handleTotalCostChange(e.value)}
              mode="currency"
              currency="USD"
              locale="en-US"
              className="w-full"
              inputClassName={receivingFieldClassName}
            />
          </div>
        </div>

        <div className="mt-6 rounded-[18px] border border-[#E2E8F0] bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <p className="text-sm font-medium text-[#0F172A] mb-3">
            Product Quality Control
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <Input
                label="Product Temperature"
                name="product_temperature"
                onChange={handleInputChange}
                value={values.product_temperature}
                labelOn={true}
                type="text"
                className={receivingFieldClassName}
              />
            </div>
            <div>
              <SelectMenu
                handleInputChange={handleInputChange}
                name="product_condition"
                options={qualityConditionOptions}
                value={values.product_condition}
                title="Product Condition"
              />
            </div>
            <div>
              <SelectMenu
                handleInputChange={handleInputChange}
                name="product_quality"
                options={qualityConditionOptions}
                value={values.product_quality}
                title="Product Quality"
              />
            </div>
            <div>
              <SelectMenu
                handleInputChange={handleInputChange}
                name="cfia_meat"
                options={yesOrNoOptions}
                value={values.cfia_meat}
                title="CFIA Meat"
              />
            </div>
            <div>
              <SelectMenu
                handleInputChange={handleInputChange}
                name="lab_results"
                options={yesOrNoOptions}
                value={values.lab_results}
                title="Lab Results"
              />
            </div>
            <div>
              <SelectMenu
                handleInputChange={handleInputChange}
                name="is_halal"
                options={yesOrNoOptions}
                value={values.is_halal}
                title="Is Halal"
              />
            </div>
            <div>
              <SelectMenu
                handleInputChange={handleInputChange}
                name="zone"
                options={zoneOptions}
                value={values.zone}
                title="Zone"
                isClearable
                placeholder={
                  invoiceLocation?.value
                    ? "Select zone…"
                    : "Set invoice location first"
                }
                disabled={!invoiceLocation?.value}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex shrink-0 justify-end gap-3 border-t border-[#E2E8F0] bg-[#F6F8FB] p-6">
        <button
          type="button"
          onClick={onCancel}
          className={receivingNeutralButtonClass}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          className={receivingPrimaryButtonClass}
        >
          Save
        </button>
      </div>
    </div>
  );
}
